import React, { useEffect, useState } from "react";
import { PencilSimple } from "@phosphor-icons/react";
import { isRTL, langLabel } from "@/lib/languages";

function fmtTs(sec) {
  if (sec == null) return "00:00:00,000";
  if (sec < 0) sec = 0;
  const ms = Math.round(sec * 1000);
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  const r = ms % 1000;
  const pad = (n, w = 2) => String(n).padStart(w, "0");
  return `${pad(h)}:${pad(m)}:${pad(s)},${pad(r, 3)}`;
}

function Row({ seg, i, rtl, onEdit, variant }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(seg.text || "");
  useEffect(() => { setDraft(seg.text || ""); }, [seg.text]);

  const save = () => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== seg.text && onEdit) onEdit(i, trimmed);
    setEditing(false);
  };
  const cancel = () => { setDraft(seg.text || ""); setEditing(false); };

  return (
    <div
      className={`p-4 ${variant === "inverse" ? "hover:bg-white/5" : "hover:bg-[--paper]"} transition-colors group`}
      onDoubleClick={() => onEdit && setEditing(true)}
    >
      <div className="flex items-center justify-between mb-1">
        <span className={`font-mono text-[11px] ${variant === "inverse" ? "text-[#7da8ff]" : "text-[--signal]"}`}>
          {fmtTs(seg.start)} → {fmtTs(seg.end)}
          {seg.speaker && <span className={`ml-2 px-1.5 py-0.5 border ${variant === "inverse" ? "border-white/30 text-white/80" : "border-[--ink]/40 text-[--ink]"}`}>{seg.speaker}</span>}
        </span>
        <div className="flex items-center gap-2">
          {onEdit && !editing && (
            <button
              onClick={() => setEditing(true)}
              className={`opacity-0 group-hover:opacity-100 ${variant === "inverse" ? "text-white/60 hover:text-white" : "text-[--muted] hover:text-[--accent]"} transition-all`}
              aria-label="Edit"
              data-testid="edit-segment-btn"
            >
              <PencilSimple weight="bold" size={14} />
            </button>
          )}
          <span className={`font-mono text-[10px] ${variant === "inverse" ? "text-white/40" : "text-[--muted]"}`}>#{seg.index ?? i + 1}</span>
        </div>
      </div>
      {editing ? (
        <textarea
          value={draft}
          autoFocus
          dir={rtl ? "rtl" : "ltr"}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) save();
            else if (e.key === "Escape") cancel();
          }}
          data-testid="edit-segment-input"
          className={`w-full text-sm leading-relaxed ${rtl ? "font-arabic text-right" : ""} ${variant === "inverse" ? "bg-white/10 text-white border border-white/30" : "bg-white border border-[--ink]"} p-2 focus:outline-none focus:ring-2 focus:ring-[--accent]`}
          rows={Math.max(2, Math.ceil((draft.length || 0) / 60))}
        />
      ) : (
        <div dir={rtl ? "rtl" : "ltr"} className={`text-sm leading-relaxed ${rtl ? "font-arabic text-right" : ""} ${variant === "inverse" ? "text-white/95" : "text-[--ink]"}`}>
          {seg.text || <span className={`italic ${variant === "inverse" ? "text-white/40" : "text-[--muted]"}`}>(empty)</span>}
        </div>
      )}
    </div>
  );
}

export default function SRTViewer({ title, segments, language, dialect, variant = "default", testId, onEdit }) {
  const rtl = isRTL(language);
  const inverse = variant === "inverse";
  return (
    <div className={inverse ? "bg-[#0f0f10] text-white" : "bg-white"} data-testid={testId || "srt-viewer"}>
      <div className={`flex items-center justify-between px-5 py-3 ${inverse ? "border-b border-white/15 bg-[--ink]" : "border-b border-[--line] bg-[--paper]"}`}>
        <div>
          <div className={`overline ${inverse ? "text-white/70" : "text-[--muted]"}`}>{title}</div>
          <div className={`text-xs font-mono ${inverse ? "text-white/60" : "text-[--muted]"}`}>
            {langLabel(language)}{dialect ? ` · ${dialect}` : ""}
          </div>
        </div>
        <div className={`font-mono text-xs ${inverse ? "text-white/50" : "text-[--muted]"}`}>
          {segments.length} segments{onEdit && " · dbl-click to edit"}
        </div>
      </div>
      <div className={`max-h-[560px] overflow-y-auto scroll-thin ${inverse ? "divide-y divide-white/10" : "divide-y divide-[--line]"}`}>
        {segments.length === 0 ? (
          <div className={`p-10 text-center overline ${inverse ? "text-white/50" : "text-[--muted]"}`}>No segments yet</div>
        ) : segments.map((s, i) => (
          <Row key={`${s.start}-${s.end}-${i}`} seg={s} i={i} rtl={rtl} onEdit={onEdit} variant={variant} />
        ))}
      </div>
    </div>
  );
}
